import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

const FLOAT_Z_INDEX = 10050
const HIDE_DELAY_MS = 220

export default function HoverTooltip({
  children,
  content,
  className = '',
  stopPropagation = false,
}) {
  const anchorRef = useRef(null)
  const floatRef = useRef(null)
  const hideTimeoutRef = useRef(null)
  const text = typeof content === 'string' ? content.trim() : String(content ?? '').trim()

  const [visible, setVisible] = useState(false)
  const [floatedCoords, setFloatedCoords] = useState(null)

  const clearHideTimer = useCallback(() => {
    if (hideTimeoutRef.current != null) {
      window.clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }, [])

  useEffect(() => () => clearHideTimer(), [clearHideTimer])

  const show = useCallback(() => {
    clearHideTimer()
    setVisible(true)
  }, [clearHideTimer])

  const scheduleHide = useCallback(() => {
    clearHideTimer()
    hideTimeoutRef.current = window.setTimeout(() => {
      setFloatedCoords(null)
      setVisible(false)
      hideTimeoutRef.current = null
    }, HIDE_DELAY_MS)
  }, [clearHideTimer])

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current
    const floated = floatRef.current
    if (!anchor || !floated || !visible) return
    const rect = anchor.getBoundingClientRect()
    const stackHeight = floated.offsetHeight || floated.getBoundingClientRect().height
    if (!stackHeight) return
    const gap = 6
    let top = rect.top - gap - stackHeight
    const pad = 8
    top = Math.max(pad, Math.min(top, window.innerHeight - stackHeight - pad))
    const left = rect.left + rect.width / 2
    setFloatedCoords({ top, left })
  }, [visible])

  useLayoutEffect(() => {
    if (!visible || !text) return undefined
    setFloatedCoords(null)
    const id = requestAnimationFrame(() => updatePosition())
    return () => cancelAnimationFrame(id)
  }, [visible, text, updatePosition])

  useEffect(() => {
    if (!visible) return undefined
    const onLayout = () => updatePosition()
    window.addEventListener('resize', onLayout)
    window.addEventListener('scroll', onLayout, true)

    let ro = null
    let aborted = false
    const rafId = requestAnimationFrame(() => {
      if (aborted) return
      const el = floatRef.current
      if (!el || typeof ResizeObserver === 'undefined') return
      ro = new ResizeObserver(onLayout)
      ro.observe(el)
    })

    return () => {
      aborted = true
      cancelAnimationFrame(rafId)
      window.removeEventListener('resize', onLayout)
      window.removeEventListener('scroll', onLayout, true)
      ro?.disconnect()
    }
  }, [visible, updatePosition])

  const stopEventBubble = stopPropagation ? (e) => e.stopPropagation() : undefined

  if (!text) return children

  const portalTarget = typeof document !== 'undefined' ? document.body : null

  const floatNode =
    visible && portalTarget
      ? createPortal(
          <div
            ref={floatRef}
            className="flex flex-col items-center"
            style={{
              position: 'fixed',
              top: floatedCoords?.top ?? -9999,
              left: floatedCoords?.left ?? -9999,
              transform: 'translateX(-50%)',
              zIndex: FLOAT_Z_INDEX,
              visibility: floatedCoords ? 'visible' : 'hidden',
            }}
            onMouseEnter={show}
            onMouseLeave={scheduleHide}
            onMouseDown={stopEventBubble}
            onClick={stopEventBubble}
          >
            <span
              role="tooltip"
              className="max-h-48 max-w-[280px] cursor-default overflow-y-auto whitespace-pre-wrap break-words rounded-lg bg-rb-dark-black px-3 py-2 text-left text-xs leading-snug text-neutral-50 shadow-xl ring-1 ring-white/10"
            >
              {text}
            </span>
            <span
              aria-hidden
              className="-mt-px block h-0 w-0 border-x-[9px] border-x-transparent border-t-[10px] border-t-rb-dark-black"
            />
          </div>,
          portalTarget,
        )
      : null

  const iconTone = visible
    ? 'text-rb-green'
    : 'text-rb-lighter-grey group-hover:text-rb-green dark:text-gray-400'

  return (
    <>
      <span
        ref={anchorRef}
        className={`group inline-flex shrink-0 cursor-pointer rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-rb-green focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${className}`}
        tabIndex={0}
        aria-expanded={visible}
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
        onFocus={show}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) {
            clearHideTimer()
            setFloatedCoords(null)
            setVisible(false)
          }
        }}
        onMouseDown={stopEventBubble}
        onClick={stopEventBubble}
      >
        <span className={`inline-flex transition-colors [&>svg]:text-xs ${iconTone}`}>
          {children}
        </span>
      </span>
      {floatNode}
    </>
  )
}
